/**
 * KAN-756: таблица CRM интернет-магазина. Не интернет-магазин: API отвечает 404.
 * KAN-788: фильтр по атрибуту лида в адресе (?attr=&attr_value=).
 * KAN-862: вынесено из CrmPage (вид "Таблица"); `leading` (переключатель вида)
 * стоит первым в строке фильтров. Запрос таблицы уходит только в этом виде.
 * KAN-880: полный список лидов из GET /api/v1/crm/leads/ страницами
 * ("Показать ещё"), без периода отработки. Исход (?outcome=), общие фильтры
 * доски (поиск, менеджер), атрибут и организация (?company_id=)
 * уходят в запрос; город и название компании фильтруются по загруженным строкам
 * (тогда счётчик - число отобранных строк, иначе total ответа).
 * KAN-885: период таблицы (useCrmTablePeriod): по умолчанию сегодня, "Всё время" -
 * без дат. При периоде строки сгруппированы по дню (KAN-973: по period_at, см. ниже).
 * KAN-887: выпадающий список "Сортировка" (?sort=, уходит в запрос), заголовки
 * дней только при периоде и сортировке по дате взятия.
 * KAN-891/892: кнопки "Продажи", "Неразобранные", "Нет движения" - переключатели
 * со счётчиками ответа. Флажка "Без движения больше 7 дней" в таблице нет.
 * KAN-883: ?event= (из "Лиды по дням") - по дню какого события отбирается период;
 * период одним полем CrmRangePicker; панель фильтров (CrmTableFilters) одна и та же
 * в обычном и развёрнутом на весь экран виде (в развёрнутом - внутри карточки).
 * KAN-898: владельцу четвёртая кнопка "Мусор (N)" (useCrmJunkView, ?junk=1): только
 * контакты в мусоре, у строк "Вернуть" вместо действий.
 * KAN-905: "Только новые лиды" (useNewLeadsOnly, общий с доской): при периоде
 * запрос с event=lead (по дню появления лида), явный ?event= главнее.
 * KAN-973: флаг выключен - запрос без event, бэкенд отдаёт новые лиды и взятые
 * в работу за период; день строки period_at (взятие в периоде или появление лида).
 * KAN-906: полосы "Клиенты N" нет: число клиентов и "Развернуть"/"Свернуть"
 * (CrmTableCount) в конце той же строки фильтров в обоих видах.
 * KAN-958: кнопка "Колонки" (CrmColumnsDialog) перед числом клиентов в обоих видах;
 * скрытые столбцы хранятся у пользователя (useCrmTableColumns).
 */
import { useEffect, useMemo, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { CrmBoardTable } from '@/components/crm/CrmBoardTable';
import { CrmColumnsDialog } from '@/components/crm/CrmColumnsDialog';
import { CrmTableCount } from '@/components/crm/CrmTableCount';
import { CrmTableFilters } from '@/components/crm/CrmTableFilters';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useCrmLeadFilters, useDropRejectedCrmManager } from '@/hooks/useCrmLeadFilters';
import { crmLeadsKey, useCrmLeads } from '@/hooks/useCrmLeads';
import { useCrmJunkView } from '@/hooks/useCrmJunkView';
import { useCrmTablePeriod } from '@/hooks/useCrmTablePeriod';
import { useCrmTableUrlFilters } from '@/hooks/useCrmTableUrlFilters';
import { useCrmFullscreen } from '@/hooks/useCrmFullscreen';
import { useCrmTableColumns } from '@/hooks/useCrmTableColumns';
import { useNewLeadsOnly } from '@/hooks/useNewLeadsOnly';
import { parseAttributeFilter } from '@/lib/crmAttributeFilter';
import { crmLeadsQuery, isParamError, leadColumnLabels, leadTableRows } from '@/lib/crmLeads';
import { cityOptions, filterRowsByCity, filterRowsByCompany } from '@/lib/crmRowFilters';
import { crmTableColumns } from '@/lib/crmTableColumns';
import { otherPeriodEvent } from '@/lib/crmTableShortcuts';
import type { CrmAttribute } from '@/types/crm';
import { groupsByTakenDay } from '@/lib/crmTableSort';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { tableNewLeadsFlags } from '@/lib/newLeadsOnly';

const MESSAGE = 'rounded-lg border bg-white p-6 text-sm';
/** Город и текст организации отбираются по загруженным страницам, не по всем лидам. */
const LOCAL_COUNT_NOTE = 'из загруженных';
const NO_ATTRIBUTES: readonly CrmAttribute[] = [];

export function CrmTableSection({ leading }: { leading?: ReactNode }): JSX.Element {
  const period = useCrmTablePeriod();
  const leadFilters = useCrmLeadFilters();
  const url = useCrmTableUrlFilters();
  const fullscreen = useCrmFullscreen();
  const junkView = useCrmJunkView();
  const { location } = url;
  const newLeads = useNewLeadsOnly(period.range);
  const flags = useMemo(
    () => tableNewLeadsFlags(url.flags, newLeads.newLeadsOnly, period.range),
    [url.flags, newLeads.newLeadsOnly, period.range]
  );
  const query = useMemo(
    () =>
      crmLeadsQuery(
        period.outcome,
        leadFilters.params,
        { attr: url.attr, attrValue: url.attrValue, companyId: location.companyId },
        period.range,
        period.sort,
        flags,
        junkView.active
      ),
    [
      period.outcome,
      leadFilters.params,
      url.attr,
      url.attrValue,
      location.companyId,
      period.range,
      period.sort,
      flags,
      junkView.active,
    ]
  );
  // Период по другому событию (не умолчанию lead_or_taken / taken): заголовки дней не рисуются.
  const otherEvent = otherPeriodEvent(flags.event) !== null;
  const leads = useCrmLeads(query);
  const { data, isLoading, error } = leads;
  const first = data?.pages[0];
  const attributes = first?.attributes;
  const attributeFilter = useMemo(
    () => parseAttributeFilter(url.attr, url.attrValue, attributes ?? []),
    [url.attr, url.attrValue, attributes]
  );
  // KAN-958: столбцы таблицы лидов (CrmTable с columnLabels) и скрытые пользователем.
  const tableColumns = useMemo(
    () => crmTableColumns({ leadMode: true, attributes: attributes ?? NO_ATTRIBUTES }),
    [attributes]
  );
  const columns = useCrmTableColumns(tableColumns);
  const allRows = useMemo(() => leadTableRows(data), [data]);
  const columnLabels = useMemo(() => leadColumnLabels(data), [data]);
  const cities = useMemo(() => cityOptions(allRows, location.city), [allRows, location.city]);
  const companyIdName = location.companyId
    ? (allRows.find((i) => i.row.client_company?.id === location.companyId)?.row.client_company?.name ?? null)
    : null;
  const localFilter = !!(location.city || location.company);
  const items = useMemo(
    () => filterRowsByCompany(filterRowsByCity(allRows, location.city), location.company),
    [allRows, location.city, location.company]
  );

  const staleAttribute = !!url.attr && isParamError(error, 'attr');
  const { clearAttribute } = url;
  useEffect(() => {
    if (staleAttribute) clearAttribute();
  }, [staleAttribute, clearAttribute]);
  useDropRejectedCrmManager(error, leadFilters);

  const expanded = fullscreen.isFullscreen;
  const attentionCount = items.filter((item) => 'attention' in item.row && item.row.attention === true).length;
  const trailing = (
    <div className="ml-auto flex shrink-0 items-center gap-1.5">
      <CrmColumnsDialog
        columns={columns.columns}
        hidden={columns.hidden}
        onToggle={columns.setVisible}
        onShowAll={columns.showAll}
      />
      <CrmTableCount
        total={first ? (localFilter ? items.length : first.total) : null}
        countNote={localFilter ? LOCAL_COUNT_NOTE : null}
        attentionCount={attentionCount}
        expanded={expanded}
        onToggleFullscreen={fullscreen.toggle}
      />
    </div>
  );

  const filters = (withLeading: boolean) => (
    <CrmTableFilters
      leading={withLeading ? leading : undefined}
      period={period}
      newLeads={newLeads}
      leadFilters={leadFilters}
      url={url}
      attributes={attributes}
      attributeFilter={attributeFilter}
      cities={cities}
      companyIdName={companyIdName}
      counts={{
        unassigned: first?.unassigned_count ?? undefined,
        idle: first?.idle_count ?? undefined,
        junk: first?.junk_count ?? 0,
      }}
      hidden={junkView.available ? [] : ['junk']}
      trailing={trailing}
    />
  );

  let status: JSX.Element | null = null;
  if (isLoading) {
    status = (
      <div className="flex justify-center py-10" data-testid="crm-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  } else if (isNotFoundError(error)) {
    status = <div className={`${MESSAGE} text-gray-600`}>CRM доступна только в режиме интернет-магазина.</div>;
  } else if (error || !first) {
    status = (
      <div className={`${MESSAGE} text-red-600`} data-testid="crm-error">
        Не удалось загрузить CRM: {extractErrorMessage(error, 'ошибка сервера')}
      </div>
    );
  }

  // Развёрнутая карточка держится и пока грузится новый запрос: панель фильтров внутри неё.
  const showCard = expanded || !status;

  return (
    <>
      {!expanded && filters(true)}
      {showCard ? (
        <CrmBoardTable
          items={items}
          queryKey={crmLeadsKey(query)}
          stages={first?.stages}
          attributes={attributes}
          columnLabels={columnLabels}
          groupByDay={!otherEvent && groupsByTakenDay(period.range, period.sort)}
          expanded={expanded}
          toolbar={expanded ? filters(false) : null}
          placeholder={status}
          junkMode={junkView.active}
          columns={columns}
          more={{
            hasMore: !!leads.hasNextPage,
            loading: leads.isFetchingNextPage,
            load: () => void leads.fetchNextPage(),
          }}
        />
      ) : (
        status
      )}
    </>
  );
}
